// Psychologist/Psychologists.tsx

import React, {useContext, useEffect, useState} from 'react';
import TabButton from 'Core/components/elements/Tabs/TabButton';
import {FRCC} from 'WideLayout/Layouts';
import MyPsychologists from "Psychologist/MyPsychologists";
import PsychologistsByRequest from "../Questionnaire/PsychologistsByRequest";
import EditIcon from "@mui/icons-material/EditRounded";
import {redirectWithNextBack} from "../../Utils/redirectNext";
import {useLocation, useNavigate} from "react-router-dom";
import {axios, DOMAIN_URL} from 'Auth/axiosConfig';
import {useErrorProcessing} from 'Core/components/ErrorProvider';
import CircularProgress from 'Core/components/elements/CircularProgress';
import {AuthContext, AuthContextType} from 'Auth/AuthContext';
import FavoritePsychologists from "Psychologist/Favorites/FavoritesPsychologists";
import {useTheme} from "Theme/ThemeContext";
import {FavoritesContext} from "Psychologist/Favorites/FavoritesContext";
import Head from "Core/components/Head";

interface PsychologistsCountResponse {
    my_psychologists_count: number;
    favorite_psychologists_count: number;
}

interface PsychologistsProps {
    containerRef?: React.RefObject<HTMLDivElement>;
}

const Psychologists: React.FC<PsychologistsProps> = ({containerRef}) => {
    const [activeTab, setActiveTab] = useState<string>('by_request');
    const [availableTabs, setAvailableTabs] = useState<string[]>(['by_request']); // 'by_request' всегда доступна
    const [loadingTabs, setLoadingTabs] = useState<boolean>(true);
    const navigate = useNavigate();
    const location = useLocation();
    const {byResponse} = useErrorProcessing();
    const {isAuthenticated, user} = React.useContext(AuthContext) as AuthContextType;
    const {plt} = useTheme();
    const {favoritePsychologists} = useContext(FavoritesContext);

    useEffect(() => {
        const existingData = JSON.parse(localStorage.getItem('clientRequest') || '{}');
        if (!existingData.age && !user?.age) navigate('/questionnaire/general/')
    }, []);

    useEffect(() => {
        const fetchTabsAvailability = async () => {
            // Если пользователь не аутентифицирован, проверяем только локальное хранилище.
            if (!isAuthenticated) {
                const tabs = ['by_request'];
                // Проверяем есть ли избранные в локал сторейдж или в состоянии контекста.
                // Если избранные психологи впервые добавлены сейчас, это будет отражено в favoritePsychologists.
                if (favoritePsychologists && favoritePsychologists.length > 0) {
                    tabs.push('favorites');
                }
                setAvailableTabs(tabs);
                setLoadingTabs(false);
                return;
            }

            // Если аутентифицирован - проверяем серверные данные и локальные.
            try {
                const response = await axios.get<PsychologistsCountResponse>('/api/v1/psychologists/my-and-favorite/count/');
                const {my_psychologists_count, favorite_psychologists_count} = response.data;

                const tabs = ['by_request'];

                // Если есть мои психологи
                if (my_psychologists_count > 0) {
                    tabs.push('my');
                }

                // Есть ли избранные по данным сервера или локальным
                // Если на сервере есть избранные или в локал-сторейдже есть избранные
                // или они были добавлены во время сессии (favoritePsychologists)
                if (favorite_psychologists_count > 0 || (favoritePsychologists && favoritePsychologists.length > 0)) {
                    tabs.push('favorites');
                }

                setAvailableTabs(tabs);

                // Если текущая активная вкладка больше не доступна, переключаемся на 'by_request'
                if (!tabs.includes(activeTab)) {
                    setActiveTab('by_request');
                }

            } catch (error) {
                byResponse(error);
                // В случае ошибки показываем только 'by_request'
                setAvailableTabs(['by_request']);
                setActiveTab('by_request');
            } finally {
                setLoadingTabs(false);
            }
        };

        // Перезапускаем проверку доступных вкладок при изменении аутентификации или списка избранных.
        fetchTabsAvailability();
    }, [isAuthenticated, byResponse, activeTab, favoritePsychologists]); // Добавили favoritePsychologists в зависимости

    const renderActiveTab = () => {
        switch (activeTab) {
            case 'by_request':
                return <PsychologistsByRequest containerRef={containerRef}/>;
            case 'my':
                return <MyPsychologists containerRef={containerRef}/>;
            case 'favorites':
                return <FavoritePsychologists containerRef={containerRef}/>;
            default:
                return null;
        }
    };

    const tabsData = [
        {id: 'by_request', label: 'По запросу'},
        {id: 'my', label: 'Мои'},
        {id: 'favorites', label: 'Избранное'},
    ];

    if (loadingTabs) {
        return (
            <FRCC mt={4}>
                <CircularProgress size={'110px'}/>
            </FRCC>
        );
    }

    return (
        <>
            <Head
                title={'Психологи | Лайф Хелп'}
                description={'Помощь квалифицированных психологов от сервиса Лайф Хелп'}
                robots={'noindex, nofollow'}
                canonicalUrl={`${DOMAIN_URL}/psychologists`}
            />
            <FRCC g={1}>
                <EditIcon
                    onClick={() => redirectWithNextBack(
                        navigate, location,
                        '/questionnaire/personal')
                    }
                    style={{marginTop: 3, cursor: 'pointer', color: plt.text.primary50}}
                />
                {tabsData
                    .filter(tab => availableTabs.includes(tab.id))
                    .map((tab) => (
                        <TabButton
                            key={tab.id}
                            active={activeTab === tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            textSx={{padding: '2px 12px'}}
                        >
                            {tab.label}
                        </TabButton>
                    ))}
            </FRCC>
            {renderActiveTab()}
        </>
    );
};

export default Psychologists;
